import log from './logService.js';

// Progression XP : doit rester aligné avec backend/services/xpRewards.service.js
const XP_FOR_LEVEL_0_TO_1 = 1;
const XP_BONUS_FAMILIER_ECLOSION = 10;
const XP_BASE_PER_LEVEL = 500;
const XP_LINEAR_GROWTH = 100;

const CHALLENGE_TYPES = {
    COMPETENCE: 'competence',
    SANS_FAUTE: 'sans_faute',
    SCORE: 'score',
    NOMBRE: 'nombre',
    GAME: 'jeu',
    DICTATION: 'dictation',
    };
  
const FREQUENCIES = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    STORY: 'story',
    NEW_PLAYER: 'new_player',
    DAILYACTIVITY: 'dailyactivity'
  };


  
  



// ============================================================================
// BADGES
// ----------------------------------------------------------------------------
// 🔴 L'ÉLIGIBILITÉ SE CALCULE AU SERVEUR, et nulle part ailleurs :
//    backend/services/badgeEligibility.service.js. Le client ne fait qu'appeler
//    POST /progression/badges/sync et afficher ce qui revient — il n'envoie
//    plus d'identifiant de badge, et le serveur n'en accepte plus.
//
// Chaque badge porte un `signal` : le nom du compteur, calculé en base, qui le
// décide. C'est un CONTRAT, vérifié au démarrage par le service d'éligibilité —
// un badge dont le signal n'existe pas fait échouer le test, au lieu de rester
// silencieusement inatteignable. C'est la leçon de l'audit du 2026-09-03 :
// 14 badges sur 38 n'étaient jamais décernés, et rien ne le disait.
//
// `sansCelebration: true` → le badge entre dans l'album sans ouvrir de modale.
// Réservé aux faits déjà célébrés ailleurs (les paliers de série ont la leur).
// ============================================================================
const BADGES = {

    // ── Progression générale ────────────────────────────────────────────────
    PROGRESSION: {
        DEBUTANT: {
            id: 'beginner',
            label: 'Premiers pas',
            description: 'A commencé son apprentissage',
            condition: 'Terminer le tutoriel',
            signal: 'onboardingTermine',
            icon: '🌱'
        },
        APPRENTI: {
            id: 'apprentice',
            label: 'Apprenti',
            description: 'A pris pied dans les quatre domaines',
            condition: 'Atteindre 30 % de maîtrise moyenne sur son programme',
            signal: 'maitriseGlobalePct',
            icon: '📚',
            reward: { coins: 50 }
        },
        EXPERT: {
            id: 'expert',
            label: 'Expert',
            description: 'A pris de l\'avance sur son programme',
            condition: 'Atteindre 60 % de maîtrise moyenne sur son programme',
            signal: 'maitriseGlobalePct',
            icon: '🎓',
            reward: { coins: 150, xp: 100 }
        },
    },

    // ── Expérience ──────────────────────────────────────────────────────────
    EXPERIENCE: {
        PREMIERE_EXPERIENCE: {
            id: 'premiere_experience',
            label: 'Première expérience',
            description: 'A obtenu 1000 XP ou atteint le niveau 2',
            condition: 'Obtenir 1000 XP ou atteindre le niveau 2',
            signal: 'niveau',
            icon: '🌱'
        },
        NIVEAU_5:  { id: 'niveau_5',  label: 'Niveau 5',  description: 'A atteint le niveau 5',  condition: 'Atteindre le niveau 5',  signal: 'niveau', icon: '🎓' },
        NIVEAU_10: { id: 'niveau_10', label: 'Niveau 10', description: 'A atteint le niveau 10', condition: 'Atteindre le niveau 10', signal: 'niveau', icon: '🎓', reward: { coins: 100 } },
        NIVEAU_20: { id: 'niveau_20', label: 'Niveau 20', description: 'A atteint le niveau 20', condition: 'Atteindre le niveau 20', signal: 'niveau', icon: '🎓', reward: { coins: 250, xp: 100 } },
        // ⚠️ Décerné au niveau 50, pas 30. Le seuil du code disait 30 jusqu'au
        // 2026-09-04 — libellé « Niveau 50 », récompense versée vingt niveaux trop tôt.
        NIVEAU_50: { id: 'niveau_50', label: 'Niveau 50', description: 'A atteint le niveau 50', condition: 'Atteindre le niveau 50', signal: 'niveau', icon: '🎓', reward: { coins: 500, xp: 200 } },
    },

    // ── Assiduité ───────────────────────────────────────────────────────────
    // Les seuils sont CALÉS sur STREAK_MILESTONE_DAYS (profile.service.ts) :
    // le palier de série ouvre déjà sa modale, le badge n'est que la trace dans
    // l'album. D'où `sansCelebration` — sinon deux modales pour le même fait.
    ASSIDUITE: {
        REGULIER: {
            id: 'regular',
            label: 'Régulier',
            description: 'Est revenu 7 jours de suite',
            condition: 'Se connecter 7 jours de suite',
            signal: 'serieConnexion',
            loginStreak: 7,
            sansCelebration: true,
            icon: '📅',
            reward: { coins: 50 }
        },
        PERSEVERANT: {
            id: 'persistent',
            label: 'Persévérant',
            description: 'Est revenu 30 jours de suite',
            condition: 'Se connecter 30 jours de suite',
            signal: 'serieConnexion',
            loginStreak: 30,
            sansCelebration: true,
            icon: '⭐',
            reward: { coins: 200, xp: 150 }
        },
        ENDURANT: {
            id: 'endurant',
            label: 'Endurant',
            description: 'Est revenu 100 jours de suite',
            condition: 'Se connecter 100 jours de suite',
            signal: 'serieConnexion',
            loginStreak: 100,
            sansCelebration: true,
            icon: '⭐',
            reward: { coins: 500, xp: 300 }
        }
    },

    // ── Constance ───────────────────────────────────────────────────────────
    // Distincte de l'assiduité : la série se casse, la constance se cumule.
    CONSTANCE: {
        MARATHON: {
            id: 'marathon',
            label: 'Marathonien',
            description: 'S\'est entraîné 100 jours, consécutifs ou non',
            condition: 'S\'entraîner 100 jours différents',
            signal: 'joursEntrainement',
            icon: '🏃',
            reward: { coins: 300, xp: 200 }
        },
        MATINAL: {
            id: 'early_bird',
            label: 'Lève-tôt',
            description: 'A commencé dix journées par un exercice',
            condition: 'S\'entraîner 10 fois avant 9 h du matin',
            signal: 'sessionsAvant9h',
            icon: '🌅',
            reward: { coins: 100 }
        },
     },

    // ── Domaines ────────────────────────────────────────────────────────────
    // ⚠️ La maîtrise d'un domaine se calcule sur TOUTES les notions du cycle de
    // l'élève, les non travaillées comptant pour 0. Jusqu'au 2026-09-04 c'était
    // la moyenne des seules notions déjà rencontrées : deux notions réussies
    // suffisaient à décrocher le badge « toutes les compétences du domaine ».
    DOMAINES: {
        GRAMMAIRE:     { id: 'grammar_expert',      label: 'Expert en grammaire',       description: 'Maîtrise les règles de grammaire',   condition: 'Atteindre 90 % de maîtrise sur la grammaire de son cycle',    signal: 'maitriseParDomainePct', domain: 'grammaire',     icon: '📝', reward: { coins: 150, xp: 100 } },
        ORTHOGRAPHE:   { id: 'spelling_master',     label: 'Maître de l\'orthographe',  description: 'Excellence en orthographe',           condition: 'Atteindre 90 % de maîtrise sur l\'orthographe de son cycle', signal: 'maitriseParDomainePct', domain: 'orthographe',   icon: '✍️', reward: { coins: 150, xp: 100 } },
        VOCABULAIRE:   { id: 'vocabulary_expert',   label: 'Expert en vocabulaire',     description: 'Maîtrise un large vocabulaire',       condition: 'Atteindre 90 % de maîtrise sur le vocabulaire de son cycle', signal: 'maitriseParDomainePct', domain: 'vocabulaire',   icon: '📚', reward: { coins: 150, xp: 100 } },
        // Créé le 2026-09-04 : le domaine existait, son badge manquait — et
        // « Maître du savoir » s'obtenait donc sans jamais toucher au quatrième.
        COMPREHENSION: { id: 'comprehension_expert', label: 'Expert en compréhension',  description: 'Comprend ce qu\'il lit',              condition: 'Atteindre 90 % de maîtrise sur la compréhension de son cycle', signal: 'maitriseParDomainePct', domain: 'comprehension', icon: '🧠', reward: { coins: 150, xp: 100 } },
        MAITRE: {
            id: 'master',
            label: 'Maître du savoir',
            description: 'A atteint l\'excellence dans les quatre domaines',
            condition: 'Obtenir les quatre badges d\'expert de domaine',
            signal: 'maitriseParDomainePct',
            icon: '👑',
            reward: { coins: 500, xp: 300 }
        },
    },

    // ── Économie ────────────────────────────────────────────────────────────
    ECONOMIE: {
        BRONZE: { id: 'COINS_COLLECTOR_BRONZE', label: 'Collectionneur Bronze', description: 'A eu 1000 pièces en bourse',  condition: 'Posséder 1000 pièces',  signal: 'pieces', icon: '🥉' },
        SILVER: { id: 'COINS_COLLECTOR_SILVER', label: 'Collectionneur Argent', description: 'A eu 5000 pièces en bourse',  condition: 'Posséder 5000 pièces',  signal: 'pieces', icon: '🥈' },
        GOLD:   { id: 'COINS_COLLECTOR_GOLD',   label: 'Collectionneur Or',     description: 'A eu 10000 pièces en bourse', condition: 'Posséder 10000 pièces', signal: 'pieces', icon: '🥇' },
    },

    // ── Aventure ────────────────────────────────────────────────────────────
    // ⚠️ PAS de badge par biome. Il y en a eu six (AVENTURIER_2…7), retirés le
    // 2026-09-04 : chaque biome donne déjà son FRAGMENT du Grand Livre, et six
    // fragments pour six biomes disaient exactement la même chose. Deux
    // récompenses pour un seul fait, c'est une de trop — et c'est la collection
    // qu'on garde, parce qu'elle se voit aussi dans l'inventaire.
    // Le suivi de fin de biome n'est pas perdu : `histoiresAchevees` en dépend,
    // et le service continue de le calculer.
    AVENTURE: {
        PREMIER_NIVEAU: {
            id: 'AVENTURIER_1',
            label: 'Aventurier',
            description: 'A terminé son premier niveau d\'aventure',
            condition: 'Terminer un niveau de l\'aventure',
            signal: 'niveauxAventureTermines',
            icon: '🌱'
        },
        HISTOIRE_ACHEVEE: {
            id: 'aventure_histoire_achevee',
            label: 'Dictoïa retrouvée',
            description: 'A mené l\'histoire de l\'Ombre et du Silence à son terme',
            condition: 'Terminer tous les biomes de l\'histoire',
            signal: 'histoiresAchevees',
            storyId: 'legacy_ombre_silence',
            icon: '📖',
            reward: { coins: 500, xp: 300 }
        },
        COLLECTION_PREMIERE: {
            id: 'aventure_premier_vestige',
            label: 'Premier vestige',
            // Le premier CRISTAL : le fragment du Grand Livre que remet le gardien de la Forêt,
            // à la fin du 1er biome — jamais une page du Livre de la Connaissance (Forêt 3).
            description: 'A rapporté le premier cristal du Grand Livre',
            condition: 'Vaincre le gardien de la Forêt',
            signal: 'fragmentsGrandLivre',
            seriesId: 'fragment_grand_livre',
            icon: '🔮',
            reward: { coins: 25 }
        },
        COLLECTION_SERIE: {
            id: 'aventure_serie_complete',
            label: 'Série complète',
            description: 'A réuni toutes les pièces d\'une série',
            condition: 'Compléter une série d\'objets d\'histoire',
            signal: 'seriesCollectiblesCompletes',
            icon: '🗝️',
            reward: { coins: 200, xp: 100 }
        },
        COLLECTION_INTEGRALE: {
            id: 'aventure_collection_integrale',
            label: 'Collection intégrale',
            description: 'N\'a laissé aucun vestige derrière lui',
            condition: 'Compléter toutes les séries d\'objets d\'une histoire',
            signal: 'histoiresCollectionneesIntegralement',
            storyId: 'legacy_ombre_silence',
            icon: '💎',
            reward: { coins: 500, xp: 300 }
        },
    },

    // ── Village ─────────────────────────────────────────────────────────────
    VILLAGE: {
        ARENE_PREMIER_DUEL: {
            id: 'village_premier_duel',
            label: 'Premier duel',
            description: 'Est entré dans l\'arène',
            condition: 'Disputer un duel dans l\'arène',
            signal: 'duelsArene',
            icon: '⚔️',
            reward: { coins: 25 }
        },
        ARENE_DIX_VICTOIRES: {
            id: 'village_dix_victoires',
            label: 'Duelliste',
            description: 'A remporté dix duels',
            condition: 'Gagner 10 duels dans l\'arène',
            signal: 'victoiresArene',
            icon: '🛡️',
            reward: { coins: 150, xp: 100 }
        },
        ARENE_ELO: {
            id: 'village_arene_1200',
            label: 'Lame affûtée',
            description: 'A porté son classement au-delà de 1200',
            condition: 'Atteindre 1200 points de classement en arène',
            signal: 'eloArene',
            icon: '🗡️',
            reward: { coins: 200, xp: 100 }
        },
        // Pas de badge « fin de saison » : `arena_season_results` se lit mais
        // rien ne l'écrit. `best_streak` est mis à jour à chaque duel.
        ARENE_SERIE: {
            id: 'village_serie_arene',
            label: 'Invaincu',
            description: 'A enchaîné cinq victoires en arène',
            condition: 'Gagner 5 duels d\'affilée',
            signal: 'meilleureSerieArene',
            icon: '🏁',
            reward: { coins: 150, xp: 100 }
        },
        // Aucun journal ne distingue un objet ACHETÉ d'un objet OFFERT : les
        // deux atterrissent dans user_owned_cosmetics sans source. Ces badges
        // portent donc sur la POSSESSION, ce qui est mesurable, et le libellé
        // le dit — pas de « premier achat » qu'on ne saurait pas prouver.
        BOUTIQUE_GARDE_ROBE: {
            id: 'village_garde_robe',
            label: 'Garde-robe fournie',
            description: 'Possède dix parures',
            condition: 'Posséder 10 objets d\'apparat différents',
            signal: 'cosmetiquesPossedes',
            icon: '🎩',
            reward: { coins: 100 }
        },
        BOUTIQUE_COFFRE: {
            id: 'village_coffre_garni',
            label: 'Coffre bien garni',
            description: 'Possède vingt-cinq parures',
            condition: 'Posséder 25 objets d\'apparat différents',
            signal: 'cosmetiquesPossedes',
            icon: '🧰',
            reward: { coins: 300, xp: 150 }
        },
    },

    // ── Familier ────────────────────────────────────────────────────────────
    // ⚠️ `NOURRISHED_FRIEND` (nourrir avec une pomme) a été RETIRÉ le
    // 2026-09-04 : rien n'enregistre l'usage d'un consommable, le badge était
    // indétectable. Il était pourtant annoté « ✅ IMPLÉMENTÉ » et son
    // déclencheur existait dans familier.store — il tirait à blanc.
    FAMILIER: {
        BON_AMI: {
            id: 'GOOD_FRIEND',
            label: 'Bon ami',
            description: 'A rendu visite à son familier dix jours',
            condition: 'Voir son familier 10 jours différents',
            signal: 'joursVisiteFamilier',
            icon: '🐣',
            reward: { coins: 50 }
        },
        GRAND_AMI: {
            id: 'GREAT_FRIEND',
            label: 'Grand ami',
            description: 'A rendu visite à son familier trente jours',
            condition: 'Voir son familier 30 jours différents',
            signal: 'joursVisiteFamilier',
            icon: '🐉',
            reward: { coins: 200, xp: 100 }
        },
        DECORE: {
            id: 'DECORATED_FRIEND',
            label: 'Tanière meublée',
            description: 'A décoré la tanière de son familier',
            condition: 'Installer un décor dans la tanière',
            signal: 'decorsTaniereEquipes',
            icon: '🎨',
            reward: { coins: 50 }
        },
        EQUIPE: {
            id: 'EQUIPPED_FRIEND',
            label: 'Équipé',
            description: 'A équipé son familier',
            condition: 'Équiper un accessoire à son familier',
            signal: 'equipementsFamilier',
            icon: '🧦',
            reward: { coins: 50 }
        },
        ADULTE:      { id: 'familier_adulte',      label: 'Compagnon adulte',    description: 'A fait grandir son familier jusqu\'à l\'âge adulte', condition: 'Faire évoluer son familier en adulte',      signal: 'niveauFamilier', icon: '🦎', reward: { coins: 100, xp: 50 } },
        ELITE:       { id: 'familier_elite',       label: 'Compagnon d\'élite',  description: 'A mené son familier au rang d\'élite',               condition: 'Faire évoluer son familier en élite',       signal: 'niveauFamilier', icon: '🦅', reward: { coins: 250, xp: 150 } },
        LEGENDAIRE:  { id: 'familier_legendaire',  label: 'Compagnon légendaire', description: 'A mené son familier jusqu\'à la légende',           condition: 'Faire évoluer son familier en légendaire',  signal: 'niveauFamilier', icon: '🐲', reward: { coins: 500, xp: 300 } },
        PREMIERE_EXPEDITION: {
            id: 'familier_premiere_expedition',
            label: 'Premier départ',
            description: 'A envoyé son familier en expédition',
            condition: 'Terminer une expédition',
            signal: 'expeditionsTerminees',
            icon: '🎒',
            reward: { coins: 25 }
        },
        DIX_EXPEDITIONS: {
            id: 'familier_dix_expeditions',
            label: 'Grand voyageur',
            description: 'A ramené le butin de dix expéditions',
            condition: 'Terminer 10 expéditions',
            signal: 'expeditionsTerminees',
            icon: '🧭',
            reward: { coins: 200, xp: 100 }
        },
        MISSION_ACCOMPLIE: {
            id: 'familier_mission_accomplie',
            label: 'Mission accomplie',
            description: 'A mené une mission de la semaine à son dernier palier',
            condition: 'Atteindre le dernier palier d\'une mission hebdomadaire',
            signal: 'missionsAccomplies',
            icon: '📜',
            reward: { coins: 150, xp: 100 }
        },
    },

    // ── Académie ────────────────────────────────────────────────────────────
    ACADEMIE: {
        PREMIERE_LECON: {
            id: 'academie_premiere_lecon',
            label: 'Cahier ouvert',
            description: 'A confié sa première leçon au Cristal',
            condition: 'Scanner une leçon',
            signal: 'leconsScannees',
            icon: '📓',
            reward: { coins: 25 }
        },
        DIX_LECONS: {
            id: 'academie_dix_lecons',
            label: 'Cahier bien tenu',
            description: 'A confié dix leçons au Cristal',
            condition: 'Scanner 10 leçons',
            signal: 'leconsScannees',
            icon: '📔',
            reward: { coins: 150, xp: 100 }
        },
        PREMIERE_QUETE: {
            id: 'academie_premiere_quete',
            label: 'Première quête',
            description: 'A mené à bien une quête forgée depuis sa leçon',
            condition: 'Terminer une quête de la Forge',
            signal: 'quetesTerminees',
            icon: '🔨',
            reward: { coins: 25 }
        },
        DIX_QUETES: {
            id: 'academie_dix_quetes',
            label: 'Forgeron du savoir',
            description: 'A mené à bien dix quêtes',
            condition: 'Terminer 10 quêtes de la Forge',
            signal: 'quetesTerminees',
            icon: '⚒️',
            reward: { coins: 200, xp: 150 }
        },
        SAGE: {
            id: 'academie_sage',
            label: 'Conseil du Sage',
            description: 'A posé sa première question au Sage',
            condition: 'Échanger avec le Sage',
            signal: 'questionsAuSage',
            icon: '🧙',
            reward: { coins: 25 }
        },
        SAGE_ASSIDU: {
            id: 'academie_sage_assidu',
            label: 'Disciple du Sage',
            description: 'A posé vingt questions au Sage',
            condition: 'Poser 20 questions au Sage',
            signal: 'questionsAuSage',
            icon: '🔮',
            reward: { coins: 150, xp: 100 }
        },
    },
};

const BADGE_CATEGORY_TITLES = {
  PROGRESSION: "Progression",
  EXPERIENCE: "Expérience",
  ASSIDUITE: "Assiduité",
  CONSTANCE: "Constance",
  DOMAINES: "Domaines",
  ECONOMIE: "Économie",
  AVENTURE: "Aventure",
  VILLAGE: "Village",
  FAMILIER: "Familier",
  ACADEMIE: "Le Sage",
};

  
const GAME_TYPES = {
    SPEED_GAME: {
      id: 'speed_game',
      label: 'Jeu de rapidité',
      subtitle: 'Tester sa vitesse',
      icon: '⚡',
      color: 'linear-gradient(45deg, #373B44, #4286f4)',
      backgroundImage: 'url("/img/background_game.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }
    },
    TEXT_HOLES: {
      id: 'text_holes',
      label: 'Texte à trous',
      subtitle: 'Compléter les textes',
      icon: '🕳️',
      color: 'linear-gradient(45deg, #FF6B6B, #FFE66D)',
      backgroundImage: 'url("/img/background_home.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }
    },
    CORRECTION_GAME: {
      id: 'correction_game',
      label: 'Correction',
      subtitle: 'Trouver les erreurs',
      icon: '🎯',
      color: 'linear-gradient(45deg, #4ECDC4, #96C93D)',
      backgroundImage: 'url("/img/background_correctiongame.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }
    },
    WORD_LISTS: {
      id: 'word_lists',
      label: 'Dictée de mots',
      subtitle: 'Réviser ses listes de mots',
      icon: '📋',
      color: 'linear-gradient(45deg, #9333EA, #C026D3)',
      backgroundImage: 'url("/img/background_game.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }

    },
    DICTATION: {
      id: 'dictation', 
      label: 'Dictée',
      subtitle: "S'entraîner aux dictées",
      icon: '📝',
      color: 'linear-gradient(45deg, #6B8DE3, #8B5CF6)',
      backgroundImage: 'url("/img/dictation-background.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }
    },
    ASSOCIATION: {
      id: 'association',
      label: 'Association par paires',
      subtitle: 'Relier les éléments',
      icon: '🔗',
      color: 'linear-gradient(45deg, #F59E0B, #FBBF24)',
      backgroundImage: 'url("/img/background_game.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }
    },
    CATEGORISATION: {
      id: 'categorisation',
      label: 'Catégorisation',
      subtitle: 'Classer dans les bonnes catégories',
      icon: '🗂️',
      color: 'linear-gradient(45deg, #10B981, #34D399)',
      backgroundImage: 'url("/img/background_game.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }
    },
    SPEED_SORT: {
      id: 'speed_sort',
      label: 'Speed Sort',
      subtitle: 'Trier rapidement',
      icon: '⚖️',
      color: 'linear-gradient(45deg, #6366F1, #818CF8)',
      backgroundImage: 'url("/img/background_game.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }
    },
    WORD_CHOICE: {
      id: 'word_choice',
      label: 'Dictée guidée',
      subtitle: 'Choisir la bonne orthographe',
      icon: '🎯',
      color: 'linear-gradient(45deg, #6B8DE3, #34D399)',
      backgroundImage: 'url("/img/dictation-background.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }
    },
    COMPREHENSION: {
      id: 'comprehension',
      label: 'Compréhension',
      subtitle: 'Lire un texte et répondre',
      icon: '📖',
      color: 'linear-gradient(45deg, #0EA5E9, #6366F1)',
      backgroundImage: 'url("/img/background_home.jpg")',
      [Symbol.toPrimitive](hint) {
        return this.id;
      }
    }
};

/** IDs canoniques (BDD / API / combat) — source de vérité unique */
const GAME_TYPE_IDS = Object.values(GAME_TYPES).map(game => game.id);

/** Sous-ensemble des jeux gestuels (association, catégorisation, speed sort) */
const GESTURE_GAME_TYPE_IDS = ['association', 'categorisation', 'speed_sort'];

/**
 * Anciens identifiants (app combat legacy, configs persistées).
 * Utiliser uniquement à la lecture ; ne plus écrire ces valeurs.
 */
const LEGACY_GAME_TYPE_IDS = {
  texteatrous: 'text_holes',
  correction: 'correction_game',
  qcm: 'speed_game',
  words: 'word_lists',
};

/** Normalise un id vers la forme canonique (BDD). Retourne l'entrée si déjà canonique. */
function normalizeGameTypeId(gameType) {
  if (gameType == null || gameType === '') return gameType;
  const key = String(gameType).trim();
  return LEGACY_GAME_TYPE_IDS[key] ?? key;
}

function isGameTypeId(gameType) {
  return GAME_TYPE_IDS.includes(normalizeGameTypeId(gameType));
}

// Fonction utilitaire pour obtenir la configuration d'un jeu par son ID
const getGameConfig = (gameId) => {
  const canonical = normalizeGameTypeId(gameId);
  return Object.values(GAME_TYPES).find(game => game.id === canonical);
};

function getGameTypeLabel(gameType) {
  const config = getGameConfig(gameType);
  return config?.label ?? gameType;
}

// Fonction utilitaire pour obtenir la liste des jeux
const getGamesList = () => Object.values(GAME_TYPES);

const DIFFICULTIES = {
    EASY: {id : 'easy', label : 'Facile'},
    MEDIUM: {id : 'medium', label : 'Moyen'},
    HARD: {id : 'hard', label : 'Difficile'},
    EXPERT: {id : 'expert', label : 'Expert'}
  };

    const ordreNiveaux = ['CP','CE1', 'CE2', 'CM1', 'CM2', '6ème', '5ème', '4ème', '3ème'];
function calculateRewards(difficulty, frequency) {
  const baseRewards = {
    easy: { xp: 25, coins: 10 },
    medium: { xp: 50, coins: 25 },
    hard: { xp: 100, coins: 50 }, 
    expert: { xp: 200, coins: 100 }
  };

  const multiplier = frequency === FREQUENCIES.WEEKLY ? 3 : 1;
  return {
    xp: baseRewards[difficulty].xp * multiplier,
    coins: baseRewards[difficulty].coins * multiplier
  };
}

// Fonction utilitaire pour obtenir le nom lisible d'une difficulté
function getDifficultyName(difficulty) {
  const difficultyMap = {
    'easy': 'Facile',
    'medium': 'Moyen',
    'hard': 'Difficile',
    'expert': 'Expert'
  };
  return difficultyMap[difficulty] || 'Moyen';
}
const GUIDED_MISSIONS = [
  { id: 'onboarding_complete',  order: 0,  xp: 0,   coins: 0  },
  { id: 'premier_combat',       order: 1,  xp: 100, coins: 20 },
  { id: 'nourrir_compagnon',    order: 2,  xp: 75,  coins: 15 },
  { id: 'visiter_compagnon',    order: 3,  xp: 50,  coins: 10 },
  { id: 'acheter_fond',         order: 4,  xp: 100, coins: 20 },
  { id: 'niveau_2',             order: 5,  xp: 150, coins: 30 },
  { id: '3_combats_serie',      order: 6,  xp: 150, coins: 30 },
  { id: 'detail_competence',    order: 7,  xp: 75,  coins: 10 },
  { id: 'combat_rapide',        order: 8,  xp: 75,  coins: 15 },
  { id: 'scanner_lecon',        order: 9,  xp: 100, coins: 20 },
  { id: 'village_visite',       order: 10, xp: 75,  coins: 20 },
  { id: 'or_competence',        order: 11, xp: 200, coins: 50 },
  { id: 'boss_biome1',          order: 12, xp: 300, coins: 75 },
  { id: 'gagner_arene',         order: 13, xp: 300, coins: 75 },
  { id: 'rejoindre_guilde',     order: 14, xp: 150, coins: 30 },
  { id: 'participer_arene',     order: 15, xp: 150, coins: 30 },
];

/** Récupère la définition d'une mission par son id. Retourne null si inconnue. */
function getGuidedMission(missionId) {
  return GUIDED_MISSIONS.find((m) => m.id === missionId) || null;
}


// ============================================================================
// CYCLE → NIVEAU SCOLAIRE — DEUX QUESTIONS DISTINCTES
//
// Un cycle couvre 3 années scolaires. Le convertir en niveau scolaire n'a donc
// pas UNE réponse mais DEUX, selon la question posée :
//
//   « Quelles notions cet enfant a-t-il le droit de voir ? »
//        → PLAFOND du cycle (cycleCeilingLevel). Il faut tout le programme du
//          cycle, donc sa dernière année. Consommé par getCompetencesforLevel,
//          adventureService, notionSelector, competenceService.
//
//   « Où parier qu'il se situe, au jour 1 ? »
//        → POSITION dans le cycle (cyclePositionIndex). Sert à amorcer
//          `user_domain_level_estimate.estimated_index`, une seule fois, à la
//          création de la ligne. Consommé par levelEstimateService uniquement.
//
// Ces deux sémantiques ont longtemps partagé une seule fonction
// (`cycleToSchoolLevel`), implémentant le plafond. L'amorçage héritait donc du
// plafond : un enfant « cycle 3 » démarrait comme s'il était déjà en 6ème, et
// les cycles 3 et 4 s'effondraient tous deux sur l'index max (5).
// Cf. Documentation/ARCHITECTURE/ARCHI_DIFFICULTE_CYCLE.md §3 (constats C1, C2, C3).
// ============================================================================

// Composition réelle des cycles français, sur l'échelle `ordreNiveaux`.
//   cycle 2 = CP(0)   · CE1(1)  · CE2(2)
//   cycle 3 = CM1(3)  · CM2(4)  · 6ème(5)
//   cycle 4 = 5ème(6) · 4ème(7) · 3ème(8)
const CYCLE_LEVELS = {
  2: ['CP', 'CE1', 'CE2'],
  3: ['CM1', 'CM2', '6ème'],
  4: ['5ème', '4ème', '3ème'],
};

const DEFAULT_CYCLE = 3;

function normalizeCycle(cycle) {
  const c = typeof cycle === 'string' ? Number(cycle) : cycle;
  return CYCLE_LEVELS[c] ? c : DEFAULT_CYCLE;
}

/**
 * PLAFOND du cycle — dernière année du cycle.
 * Répond à « quelles notions cet enfant peut-il voir ? ». `getCompetencesforLevel`
 * étant cumulatif (CP → niveau donné), passer le plafond expose tout le programme
 * du cycle, ni plus ni moins.
 *
 *   cycle 2 → 'CE2'   cycle 3 → '6ème'   cycle 4 → '3ème'
 *
 * @param {number|string} cycle - 2 | 3 | 4
 * @returns {string} clé de `competenceConfig`
 */
function cycleCeilingLevel(cycle) {
  const levels = CYCLE_LEVELS[normalizeCycle(cycle)];
  return levels[levels.length - 1];
}

// Position d'amorçage par cycle. Année MÉDIANE pour les cycles 3 et 4 : le cycle
// étant la seule information déclarée, le milieu minimise l'erreur maximale (un an
// au plus, dans un sens ou dans l'autre), là où le plafond surestime toujours.
//
// ⚠️ Le cycle 2 déroge : il reste sur CE2, son plafond. Non par principe mais par
// mesure — les constats C1/C2/C3 ne visent que les cycles 3 et 4, et le cycle 2
// se comporte correctement aujourd'hui. Passer à CE1 aurait fait tomber sa part de
// formulations `medium` de 49 % à 11 % (projeté sur les données de prod du
// 2026-07-21, cf. ARCHI_DIFFICULTE_CYCLE.md §11.4) — un changement pédagogique
// réel qu'aucun défaut ne réclamait. On corrige ce qui est cassé, rien de plus.
const CYCLE_POSITION = {
  2: 'CE2',   // plafond du cycle — exception assumée, cf. ci-dessus
  3: 'CM2',   // médiane de CM1·CM2·6ème
  4: '4ème',  // médiane de 5ème·4ème·3ème
};

/**
 * POSITION dans le cycle — où parier que cet enfant se situe, au jour 1 ?
 * Amorce `user_domain_level_estimate.estimated_index`, une seule fois.
 *
 *   cycle 2 → 'CE2'   cycle 3 → 'CM2'   cycle 4 → '4ème'
 *
 * @param {number|string} cycle - 2 | 3 | 4
 * @returns {string} clé de `competenceConfig`
 */
function cyclePositionLevel(cycle) {
  return CYCLE_POSITION[normalizeCycle(cycle)];
}

/**
 * POSITION dans le cycle, exprimée en index `ordreNiveaux` (0 = CP … 8 = 3ème).
 * Valeur d'amorçage de `user_domain_level_estimate.estimated_index`.
 *
 *   cycle 2 → 2 (CE2)   cycle 3 → 4 (CM2)   cycle 4 → 7 (4ème)
 *
 * @param {number|string} cycle - 2 | 3 | 4
 * @returns {number} index dans `ordreNiveaux`
 */
function cyclePositionIndex(cycle) {
  return ordreNiveaux.indexOf(cyclePositionLevel(cycle));
}

// DÉPART dans le cycle = sa PREMIÈRE classe (hors CP). L'app ne connaît que le cycle : un
// joueur neuf est supposé en DÉBUT de cycle, et son niveau estimé monte avec ses réussites.
// Une seule hypothèse de départ pour l'escalier, l'exploration (`mesureNotions.PREMIERE_CLASSE`)
// et l'amorçage de `user_domain_level_estimate.estimated_index` (levelEstimateService),
// depuis le 2026-09-25 (audit CYCLE_CLASSE_DIFFICULTE, I-3 : l'amorçage se faisait sur la
// position CE2 / CM2 / 4ème, et au cycle 2 l'index ne pouvait que descendre).
// ⚠️ Distinct de `cyclePositionLevel` (année médiane), qui garde ses autres usages : plafond
// du curseur de l'escalier, fiches, Elo, profil IA.
const CYCLE_DEPART = {
  2: 'CE1', // et non CP : le CP (apprendre à lire) est hors périmètre
  3: 'CM1',
  4: '5ème',
};

/**
 * DÉPART dans le cycle — première classe (hors CP) : CE1 / CM1 / 5ème.
 * @param {number|string} cycle - 2 | 3 | 4
 * @returns {string}
 */
function cycleStartLevel(cycle) {
  return CYCLE_DEPART[normalizeCycle(cycle)];
}

/**
 * DÉPART dans le cycle, en index `ordreNiveaux` : cycle 2 → 1 · cycle 3 → 3 · cycle 4 → 6.
 * @param {number|string} cycle
 * @returns {number}
 */
function cycleStartIndex(cycle) {
  return ordreNiveaux.indexOf(cycleStartLevel(cycle));
}

/**
 * @deprecated Ambigu : ne dit pas laquelle des deux questions il répond.
 * Utiliser `cycleCeilingLevel` (pool de notions) ou `cyclePositionLevel`
 * (amorçage du niveau estimé). Conservé comme alias du plafond — sémantique
 * historique de cette fonction — pour ne pas déplacer ses ~15 appelants.
 */
function cycleToSchoolLevel(cycle) {
  return cycleCeilingLevel(cycle);
}

/**
 * Retourne la récompense associée à un badge, ou null si aucune.
 * Parcourt toutes les catégories de BADGES pour trouver l'entrée par id.
 */
function getBadgeReward(badgeId) {
  for (const category of Object.values(BADGES)) {
    if (typeof category !== 'object' || category === null) continue;
    for (const badge of Object.values(category)) {
      if (typeof badge === 'object' && badge !== null && badge.id === badgeId) {
        return badge.reward || null;
      }
    }
  }
  return null;
}

// ============================================================================
// MOTEUR DE SÉLECTION D'EXERCICES — schémas déclaratifs partagés (back/front)
// Conception : Documentation/ARCHITECTURE/ARCHI_SELECTION_EXERCICES.md (§5).
// Vivent ici car `cost` doit être lu côté frontend (runtime playability /
// getSubActionCount). Source = ce fichier (frontend), copié vers le backend
// au déploiement (cf. scripts/sync-shared-config.js).
// ============================================================================

/**
 * EXERCISE_CATALOG — ce que chaque type d'exercice EST (traits intrinsèques).
 * Axes (cf. archi §5.1) :
 *  - modes   : axe pédagogique (Bloom révisé + Eduscol) — recognition →
 *              discrimination → application → production. Un type peut en porter plusieurs.
 *  - unit    : unité linguistique du stimulus — word ⊂ phrase ⊂ text, plus 'any' (joker QCM).
 *  - pacing  : 'fast' (réflexe) | 'reflective' (posé).
 *  - cost    : coût d'interaction — atomic < sequential < gesture < typing. Clé des quotas de session.
 *  - llm     : fiabilité de génération — 'reliable' | 'fragile' (fragile = exclu de l'éligibilité).
 *
 * speed_game = NOYAU SÛR : QCM universel (unit:'any'), toujours candidat → aucun slot vide.
 */
const EXERCISE_CATALOG = {
  speed_game:      { modes: ['recognition', 'discrimination'], unit: 'any',    pacing: 'fast',       cost: 'atomic',     llm: 'reliable' },
  // Deux déclarations étaient fausses, et elles se compensaient : le mode faux le rendait
  // éligible partout (toutes les compétences accordent `recognition`), l'unité fausse le
  // ré-excluait de toute la grammaire — alors que 1 133 exercices y existent et marchent.
  //   modes : l'élève TAPE le mot (cost:'typing') ; reconnaître, c'est choisir parmi des
  //           options. C'est de l'application (produire UNE forme d'après une consigne),
  //           qui sert aussi les créneaux de production.
  //   unit  : 'word' décrivait la RÉPONSE, pas le STIMULUS. L'item embarque un indice ET
  //           une phrase d'exemple (« verbe crier (subjonctif présent) » / « Bien qu'il
  //           crie fort, personne n'écoute. ») → le contexte est de niveau phrase.
  word_lists:      { modes: ['application', 'production'],      unit: 'phrase', pacing: 'fast',       cost: 'typing',     llm: 'reliable' },
  // derivesFrom : word_choice n'a PAS de pool propre — il est DÉRIVÉ d'une dictée au service
  // (on prend une dictée du pool et on fabrique les distracteurs). Son stock servable est donc
  // celui de `dictation`. Le trait rend cette dépendance déclarative : le service (route
  // word_choice pool-only) et les sondes de servabilité résolvent le pool source via
  // `poolGameTypeFor()` — sinon word_choice paraît vide (0 ligne gameType='word_choice').
  // Champ ignoré côté frontend (comme les autres traits back), lu seulement par tooling/service.
  word_choice:     { modes: ['recognition', 'discrimination'], unit: 'phrase', pacing: 'reflective', cost: 'gesture',    llm: 'reliable', derivesFrom: 'dictation' },
  association:     { modes: ['recognition'],                   unit: 'phrase', pacing: 'fast',       cost: 'gesture',    llm: 'reliable' },
  speed_sort:      { modes: ['discrimination'],                unit: 'phrase', pacing: 'fast',       cost: 'gesture',    llm: 'reliable' },
  categorisation:  { modes: ['discrimination'],                unit: 'phrase', pacing: 'reflective', cost: 'gesture',    llm: 'reliable' },
  text_holes:      { modes: ['application'],                   unit: 'text',   pacing: 'reflective', cost: 'sequential', llm: 'reliable' },
  dictation:       { modes: ['production'],                    unit: 'text',   pacing: 'reflective', cost: 'typing',     llm: 'reliable' },
  // correction_game : llm 'fragile' (le LLM ne sait pas produire des erreurs collant à la
  // notion) → rouvert UNIQUEMENT là où un chemin DÉTERMINISTE existe (whitelist ci-dessous) ;
  // le selector bypasse alors le filtre llm/mode pour ce type.
  // C'est un jeu d'IDENTIFICATION, pas de production (décidé avec Marc, 2026-07-23) : l'élève
  // repère/corrige une cible dans la phrase (mode `identify`, « Le plombier est (astucieux /
  // bête)… »). On le type donc `discrimination` / unit `phrase` → présenté comme les autres
  // jeux dans les créneaux CORE, JAMAIS comme climax capstone (le capstone privilégie
  // dictée > word_lists > text_holes, cf. exercise/selector.CAPSTONE_PRIORITY). Le variant
  // `correct_errors` (réécriture) reste généré, mais la SÉLECTION traite correction en identification.
  correction_game: { modes: ['discrimination'],                unit: 'phrase', pacing: 'reflective', cost: 'sequential', llm: 'fragile' },
};

/**
 * Formats réservés à la suite de l'aventure : `{ gameType: rang du biome (1 = premier) }`.
 * Un format listé n'est jamais servi dans un biome de rang inférieur, dans aucune histoire
 * (lu par `adventureService.generateExerciseConfigForLevel` → `selector.selectExercises`).
 * speed_sort (Marc, 2026-10-06) : 11 à 29 % de réponses justes sur la Forêt, à tous ses
 * niveaux, malgré la rampe de temps de septembre — c'est un geste complexe, il arrive avec
 * la Grotte.
 */
const FORMATS_INTRODUITS_AU_BIOME = { speed_sort: 2 };

/**
 * Les contextes d'une partie, tels que l'app les déclare (lancement `POST /combats/lancement`,
 * résultat). Le type `ContexteCombat` de l'app et la validation du serveur les lisent ici.
 */
const CONTEXTES_COMBAT = [
  'aventure', 'academie', 'entrainement', 'village', 'libre', 'rapide', 'costumier',
  'onboarding', 'onboarding_combat_1', 'onboarding_combat_2', 'guilde', 'combat',
];

/**
 * Calibration de l'HISTOIRE PRINCIPALE par biome (Marc, 2026-10-06, sujet « Rétention de
 * l'aventure ») — une DONNÉE au même titre que la difficulté et le tempo des niveaux, lue par
 * l'app (`config/scenario.meta`) et visible du serveur et de l'admin.
 *  - `budgetErreurs` : part d'erreurs tolérée sur les questions JOUÉES (rattrapage final), un
 *    PLANCHER par biome : la moitié dans la Forêt, 40 % dans la Grotte, ensuite la difficulté.
 *  - `echeance` : plancher du facteur d'échéance des scènes en temps réel (fuite, wagonnet…).
 * `rangBiome` = rang du biome dans son histoire, à partir de 1 (prologue et épilogue exclus).
 */
const HISTOIRE_PRINCIPALE_CALIBRATION = {
  storyId: 'legacy_ombre_silence',
  budgetErreurs: [
    { rangBiome: 1, errorAllowance: 0.5 },
    { rangBiome: 2, errorAllowance: 0.4 },
  ],
  echeance: [
    { rangBiome: 1, facteurEcheance: 1.8 },
    { rangBiome: 2, facteurEcheance: 1.5 },
  ],
};

/** Ordre des coûts d'interaction (atomic le plus léger). Source unique pour le runtime frontend. */
const INTERACTION_COST_RANK = { atomic: 0, sequential: 1, gesture: 2, typing: 3 };

/** Ordre des unités linguistiques. 'any' = joker (satisfait n'importe quel minUnit). */
const UNIT_RANK = { word: 0, phrase: 1, text: 2 };

/**
 * NOTION_PROFILE — ce que la notion EXIGE / PERMET, au niveau `competence`.
 * Clé : `${domain}.${competence}`. Override au niveau notion possible via
 * `${domain}.${competence}.${notion}` (rare, seulement en cas de déviation).
 *  - minUnit       : plus petite unité de stimulus que la notion exige (exclut les types bornés en dessous).
 *  - affordedModes : modes cognitifs que la notion peut légitimement faire travailler.
 *  - angle (opt.)  : consigne pédagogique transmise au prompt de génération (guide *comment*, pas *si*).
 * Fallback générique si une competence manque (cf. resolveNotionProfile côté moteur).
 */
/**
 * VITRAUX — réglages de la boucle de progression (concept vitraux, décisions
 * du 2026-07-30). PARTAGÉ front/back : le front s'en sert pour l'affichage
 * (quartier acquis, fusion), le back pour persister paliers et récompenser
 * les registres complets — les deux DOIVENT lire la même valeur.
 *  - seuilAcquisition        : maîtrise à partir de laquelle une notion est « acquise »
 *  - partFusion              : part des notions acquises qui fusionne un fragment (1 = toutes)
 *  - registreRecompensePieces: pièces d'or créditées à la complétion d'un registre
 *    (provisoire — à terme, pièces d'équipement familier spécifiques, cf. backlog vitraux)
 */
/**
 * Décors de village posés SIMULTANÉMENT — plafond.
 *
 * 🔴 Un plafond, pas une limite technique. Le modèle sait en porter autant qu'il
 * y a de familles (21 aujourd'hui) ; posés tous ensemble, le village devient un
 * bric-à-brac où plus rien ne se remarque — et chaque décor cesse d'être un
 * CHOIX. Cinq places obligent à préférer, ce qui est précisément ce qui donne du
 * prix à celui qu'on vient d'acheter.
 *
 * ⚠️ Appliqué au SERVEUR (`PUT /cosmetics/update-config`), l'écran ne faisant que
 * le refléter : une limite tenue par la seule interface n'en est pas une.
 */
const VILLAGE_DECORS_MAX = 5;

const VITRAIL = {
  seuilAcquisition: 0.9,
  partFusion: 1,
  registreRecompensePieces: 500,
};

/**
 * Les DÉCORS disponibles pour chaque scénario de jeu, par identifiant.
 *
 * Un niveau d'aventure porte deux colonnes : `type_scenario` (la mise en scène) et
 * `theme_visuel` (le décor de cette mise en scène). L'admin doit pouvoir proposer le
 * second en fonction du premier — sans quoi le décor ne se pose que par un UPDATE à la
 * main, ce qui a été le cas jusqu'ici.
 *
 * 🔴 **La source de vérité de l'ART reste le renderer** (`SCENARIO_THEMES` dans
 * `ScenarioArena`, et les registres de chaque scénario) : c'est lui qui sait dessiner.
 * Cette table-ci n'existe que pour être PARTAGÉE avec l'admin, qui ne peut pas importer de
 * composants React Native. Les laisser diverger ferait proposer à l'admin un décor que
 * l'app ne sait pas rendre, et qui retomberait en silence sur celui par défaut.
 *
 * ⚠️ Un test compare les deux pour `wagonnet_fou` (`__tests__/config/mineCartThemes.test.ts`)
 * et pour `avalanche` (`__tests__/config/avalancheThemes.test.ts`) : leur registre vit dans un
 * module à part, importable sans monter React. Les autres scénarios gardent leur registre
 * DANS leur renderer, qu'aucun test ne peut charger tant que le mock Reanimated du harnais
 * est cassé — pour ceux-là, l'accord se tient à la main.
 *
 * Un scénario absent, ou une liste vide, veut dire « pas de décor à choisir ».
 */
const SCENARIO_THEMES = {
  combat_monstre: ['classic', 'foret', 'grotte', 'chateau', 'marais', 'plaine', 'pic'],
  chemin_foret: ['classic', 'lueur', 'train'],
  course: ['classic', 'jungle', 'lueur'],
  crochetage: ['classic', 'lueur', 'train', 'odyssee_bronze'],
  infiltration: ['castle', 'goblin', 'lueur', 'train', 'odyssee_ithaque'],
  defense_zone: ['barricade', 'portcullis', 'caverne', 'magic_scroll'],
  examen_sage: ['classic', 'sanctuary', 'marais', 'lueur', 'train', 'odyssee_circe'],
  fabrication: ['costumier', 'train'],
  wagonnet_fou: ['cristal', 'neige', 'foret', 'train'],
  duel: ['arene', 'foret', 'grotte', 'chateau', 'marais', 'plaine', 'pic', 'lueur', 'train'],
  // Scénarios diégétiques (2026-09-09) — chaque renderer remplit sa ligne avec SON registre.
  // `Carrefour/carrefour.themes.ts` — test `__tests__/config/carrefourThemes.test.ts`.
  carrefour: ['foret', 'marais', 'chateau'],
  // `PontPaires/pontPaires.themes.ts` — test `__tests__/config/pontPairesThemes.test.ts`.
  // Un décor n'existe que quand son fond peint existe (`pontPaires.themes.ts`).
  pont_paires: ['riviere', 'grotte', 'montagne', 'marais', 'plaine', 'lueur', 'train'],
  // `Avalanche/avalanche.themes.ts` — test `__tests__/config/avalancheThemes.test.ts`.
  avalanche: ['rochers', 'neige', 'cristal', 'train'],
  // `Fuite/fuite.themes.ts` et `CoursePoursuite/coursePoursuite.themes.ts` — test `__tests__/config/poursuiteThemes.test.ts`.
  fuite: ['foret', 'chateau', 'grotte', 'marais'],
  course_poursuite: ['classic', 'jungle'],
};

/** Décors d'un scénario, `[]` si le scénario n'en propose pas. */
function getScenarioThemes(typeScenario) {
  return SCENARIO_THEMES[typeScenario] || [];
}

/**
 * Les TEMPOS qu'un scénario sait jouer, et son tempo par défaut
 * (`Documentation/IMPLEMENTATION/PLAN_IMPL_TEMPO_SCENARIOS.md`).
 *
 *  - `temps_reel` : la scène impose son horloge — l'obstacle frappe à l'échéance, sans
 *    consommer la question ;
 *  - `libre`      : rien ne frappe ; seul le score paie le retard.
 *
 * Ici, et pas dans l'app seule, parce que l'ADMIN (sélecteur « Tempo » d'un niveau) et le
 * SERVEUR (forme de session) doivent lire la même capacité que les renderers.
 *
 * 🔴 **Ne déclarer un tempo que quand le renderer sait le jouer.** Un niveau qui demande
 * `libre` à un scénario qui ne sait que le temps réel retombe sur le défaut ; le déclarer
 * trop tôt ferait sélectionner des exercices « calmes » pour une scène qui frappe.
 */
const TEMPS_REEL_SEUL = { tempos: ['temps_reel'], defaut: 'temps_reel' };
const LIBRE_SEUL = { tempos: ['libre'], defaut: 'libre' };
// Temps réel par défaut, temps libre sur demande du niveau (le renderer sait jouer les deux).
const DEUX_TEMPOS = { tempos: ['temps_reel', 'libre'], defaut: 'temps_reel' };
const SCENARIO_TEMPOS = {
  // L'horloge est la scène : la horde, l'obstacle, le rocher, le poursuivant, le rival.
  // Le wagonnet et la course poursuite savent aussi le temps libre (L-T4) : l'obstacle
  // s'arrête devant le wagonnet, le rival ne touche jamais le coffre. La défense de zone le
  // sait aussi depuis le 2026-10-06 (Marc : la horde abattait la barricade d'un enfant qui
  // répondait juste, Forêt 3 et 8) : les ennemis attendent devant la barricade, comme en
  // temps illimité. La fuite reste temps réel (T-D6) ; l'avalanche attend sa refonte.
  defense_zone: DEUX_TEMPOS,
  wagonnet_fou: DEUX_TEMPOS,
  avalanche: TEMPS_REEL_SEUL,
  fuite: TEMPS_REEL_SEUL,
  course_poursuite: DEUX_TEMPOS,
  // Au tour par tour : rien ne bouge tant que l'enfant n'a pas répondu.
  combat_monstre: LIBRE_SEUL,
  chemin_foret: LIBRE_SEUL,
  course: LIBRE_SEUL,
  crochetage: LIBRE_SEUL,
  fabrication: LIBRE_SEUL,
  compagnon_humeur: LIBRE_SEUL,
  infiltration: LIBRE_SEUL,
  examen_sage: LIBRE_SEUL,
  duel: LIBRE_SEUL,
  carrefour: LIBRE_SEUL,
  pont_paires: LIBRE_SEUL,
};

/** Capacité de tempo d'un scénario ; un scénario inconnu est au tour par tour. */
function getScenarioTempos(typeScenario) {
  return SCENARIO_TEMPOS[typeScenario] || LIBRE_SEUL;
}

export default {
  VILLAGE_DECORS_MAX,
  VITRAIL,
  SCENARIO_THEMES,
  getScenarioThemes,
  SCENARIO_TEMPOS,
  getScenarioTempos,
  XP_FOR_LEVEL_0_TO_1,
  XP_BONUS_FAMILIER_ECLOSION,
  XP_BASE_PER_LEVEL,
  XP_LINEAR_GROWTH,
  CHALLENGE_TYPES,
  FREQUENCIES,
  GAME_TYPES,
  GAME_TYPE_IDS,
  GESTURE_GAME_TYPE_IDS,
  LEGACY_GAME_TYPE_IDS,
  normalizeGameTypeId,
  isGameTypeId,
  getGameTypeLabel,
  DIFFICULTIES,
  calculateRewards,
  getGameConfig,
  getGamesList,
  BADGES,
  BADGE_CATEGORY_TITLES,
  ordreNiveaux,
  getDifficultyName,
  GUIDED_MISSIONS,
  getGuidedMission,
  cycleToSchoolLevel,
  cycleCeilingLevel,
  cyclePositionLevel,
  cyclePositionIndex,
  cycleStartLevel,
  cycleStartIndex,
  CYCLE_LEVELS,
  getBadgeReward,
  EXERCISE_CATALOG,
  FORMATS_INTRODUITS_AU_BIOME,
  CONTEXTES_COMBAT,
  HISTOIRE_PRINCIPALE_CALIBRATION,
  INTERACTION_COST_RANK,
  UNIT_RANK,
};

   

   
